import { useState } from 'react'
import axios from 'axios'
import logoUrl from '/formHeader.png'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface FormData {
  full_name: string
  phone: string
  availabilities: string
  presentation: string
}

const initialFormData: FormData = {
  full_name: '',
  phone: '',
  availabilities: '',
  presentation: '',
}

const inputClass =
  'w-full bg-[#1e293b] border border-[#334155] rounded-md px-4 py-3 text-[#f1f5f9] text-sm ' +
  'placeholder-[#64748b] transition-all duration-200 ' +
  'hover:border-[#475569] hover:bg-[#334155] ' +
  'focus:outline-none focus:border-[#3b82f6] focus:bg-[#334155] focus:shadow-[0_0_0_1px_rgba(59,130,246,0.2)]'

const labelClass = 'block text-sm font-medium text-[#94a3b8] mb-2'

export default function App() {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const validate = (): string | null => {
    if (!formData.full_name.trim()) {
      return 'Le nom et le prénom sont obligatoires.'
    }
    if (!formData.phone.trim()) {
      return 'Le numéro de téléphone est obligatoire.'
    }
    if (!formData.phone.startsWith('555-')) {
      return 'Le numéro de téléphone doit commencer par 555-.'
    }
    if (!formData.availabilities.trim()) {
      return 'Les disponibilités sont obligatoires.'
    }
    if (!formData.presentation.trim()) {
      return 'La présentation est obligatoire.'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    const error = validate()
    if (error) {
      setMessage({ type: 'error', text: error })
      return
    }

    setIsLoading(true)

    try {
      await axios.post(`${API_URL}/api/recruitment`, formData)
      setIsSubmitted(true)
      setFormData(initialFormData)
    } catch (err) {
      const errorText =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : 'Une erreur est survenue lors de l\'envoi de votre candidature.'
      setMessage({ type: 'error', text: errorText })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f172a]">
      <div className="w-full max-w-2xl bg-[#1e293b] rounded-xl shadow-2xl border border-[rgba(51,65,85,0.5)] overflow-hidden">
        <div className="w-full h-48 overflow-hidden">
          <img
            src={logoUrl}
            alt="Benny's - Recrutement"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="p-8">
          <div className="mb-8 text-center">
            <p className="text-[#94a3b8] text-sm font-medium">Formulaire de recrutement</p>
          </div>

          {message && (
            <div
              className={`mb-6 p-4 rounded-md text-sm ${
                message.type === 'success'
                  ? 'bg-green-900/30 text-green-400 border border-green-800'
                  : 'bg-red-900/30 text-red-400 border border-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          {isSubmitted ? (
            <div className="text-center py-8">
              <p className="text-green-400 text-lg font-medium">
                Candidature envoyée avec succès. <br />Nous vous recontacterons prochainement.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="full_name" className={labelClass}>
                  Nom et prénom
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Votre nom et prénom"
                />
              </div>

              <div>
                <label htmlFor="phone" className={labelClass}>
                  Téléphone
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-4 py-3 rounded-l-md border border-r-0 border-[#334155] bg-[#334155] text-[#94a3b8] text-sm font-medium">
                    555-
                  </span>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={formData.phone.startsWith('555-') ? formData.phone.slice(4) : formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '')
                      setFormData((prev) => ({ ...prev, phone: `555-${value}` }))
                    }}
                    className={`${inputClass} rounded-l-none`}
                    placeholder="1234"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="availabilities" className={labelClass}>
                  Disponibilités (horaires de travail)
                </label>
                <textarea
                  id="availabilities"
                  name="availabilities"
                  value={formData.availabilities}
                  onChange={handleChange}
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="Indiquez vos disponibilités horaires..."
                />
              </div>

              <div>
                <label htmlFor="presentation" className={labelClass}>
                  Brève présentation / description
                </label>
                <textarea
                  id="presentation"
                  name="presentation"
                  value={formData.presentation}
                  onChange={handleChange}
                  rows={6}
                  className={`${inputClass} resize-none`}
                  placeholder="Parlez-nous de vous, de votre expérience et de votre motivation..."
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#60a5fa] hover:bg-[#3b82f6] text-[#0f172a] font-bold py-3.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Envoi en cours...' : 'Envoyer ma candidature'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
